import type { HistoryEventRecord } from '../../domain/types.js';
import type { PersistedRuntimeState, RuntimeEventObservation } from './types.js';
import { MISSED_ACTIVITY_THRESHOLD_MS } from './types.js';

function obsMap(state: PersistedRuntimeState): Map<string, RuntimeEventObservation> {
  return new Map(state.eventObservations.map((o) => [o.eventId, o]));
}

export function markEventUserObserved(
  state: PersistedRuntimeState,
  eventId: string,
): PersistedRuntimeState {
  const map = obsMap(state);
  const prev = map.get(eventId);
  map.set(eventId, {
    eventId,
    userObserved: true,
    missedWhileAway: prev?.missedWhileAway ?? false,
  });
  return { ...state, eventObservations: [...map.values()] };
}

export function markEventMissedWhileAway(
  state: PersistedRuntimeState,
  eventId: string,
): PersistedRuntimeState {
  const map = obsMap(state);
  const prev = map.get(eventId);
  if (prev?.userObserved) return state;
  map.set(eventId, { eventId, userObserved: false, missedWhileAway: true });
  return { ...state, eventObservations: [...map.values()] };
}

export function missedEventIds(state: PersistedRuntimeState): string[] {
  const obs = obsMap(state);
  return state.historyEvents
    .map((e) => e.eventId)
    .filter((id) => {
      const o = obs.get(id);
      return o?.missedWhileAway === true && o.userObserved === false;
    });
}

export function missedHistoryEvents(state: PersistedRuntimeState): HistoryEventRecord[] {
  const ids = new Set(missedEventIds(state));
  return state.historyEvents.filter((e) => ids.has(e.eventId));
}

export function tagMissedEventsAfterAbsence(
  state: PersistedRuntimeState,
  awayMs: number,
  lastVisitIso = state.lastVisitedAt,
): PersistedRuntimeState {
  if (awayMs < MISSED_ACTIVITY_THRESHOLD_MS) return state;
  const cutoff = new Date(lastVisitIso).getTime();
  let next = state;
  for (const evt of state.historyEvents) {
    if (new Date(evt.occurredAt).getTime() <= cutoff) continue;
    next = markEventMissedWhileAway(next, evt.eventId);
  }
  return next;
}

export function acknowledgeMissedEvents(state: PersistedRuntimeState): PersistedRuntimeState {
  let next = state;
  for (const id of missedEventIds(state)) {
    next = markEventUserObserved(next, id);
  }
  return {
    ...next,
    missedPromptDismissedAt: new Date().toISOString(),
  };
}

export function shouldShowMissedPrompt(state: PersistedRuntimeState, awayMs: number): boolean {
  return awayMs >= MISSED_ACTIVITY_THRESHOLD_MS && missedEventIds(state).length > 0;
}
